import apiAuthClient from '@/services/apiAuthClient';
import apiClient from '@/services/apiClient';

import errorHandler from '@/utilities/serverErrorHandler';

const endPoints = {
  directors: {
    add: 'icapplication/directors',
    edit: 'icapplication/directors',
    get: 'icapplication/directors',
    fieldId: 'ua_icapplicationdirectorsid'
  },
  activities: {
    add: 'icapplication/activities',
    edit: 'icapplication/activities',
    get: 'icapplication/activities',
    fieldId: 'ua_icactivitylineid'
  },
  expertise: '',
  extraFunds: '',
  competitors: {
    add: 'icapplication/competitors',
    edit: 'icapplication/competitors',
    get: 'icapplication/competitors',
    fieldId: 'ua_icapplicationmaincompetitorid'
  },
  jobs: {
    add: 'icapplication/forecastjobs',
    edit: 'icapplication/forecastjobs',
    get: 'icapplication/forecastjobs',
    fieldId: 'ua_icprojectforecastjobsid'
  },
  assets: {
    add: 'icclaims/assets',
    edit: 'icclaims/assets',
    get: 'icclaims/assets',
    fieldId: 'ua_icassetid'
  },
  externalFunding: {
    add: 'icapplication/externalfunding',
    edit: 'icapplication/externalfunding',
    get: 'icapplication/externalfunding',
    fieldId: 'ua_icapplicationexternalfundingid'
  },
  shareholders: {
    add: 'icapplication/shareholders',
    edit: 'icapplication/shareholders',
    get: 'icapplication/shareholders',
    fieldId: 'ua_icapplicationshareholderid'
  }
};

export const childEntities = {
  namespaced: true,
  state: {
    directors: [],
    activities: [],
    expertise: [],
    externalFunding: [],
    competitors: [],
    jobs: [],
    assets: [],
    activityTypes: [],
    verifiedSuppliers: [],
    shareholders: [],
    currentItemId: ''
  },
  mutations: {
    ADD (state, payload) {
      const currentState = state[payload.itemType];
      currentState.push(payload.data);
      state[payload.itemType] = currentState;
    },
    UPDATE (state, payload) {
      state[payload.itemType] = payload.data;
    },
    SET_CURRENT_ID (state, editingId) {
      state.currentItemId = editingId;
    },
    UPDATE_ACTIVITY_TYPES (state, payload) {
      state.activityTypes = payload;
    },
    ADD_VERIFIED_SUPPLIER (state, payload) {
      state.verifiedSuppliers = payload;
    }
  },
  actions: {
    fetchItems ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Fetching your items...' }, { root: true });
      const method = payload === 'externalFunding' || payload === 'shareholders' || payload === 'activities' || payload === 'competitors' || payload === 'jobs' || payload === 'directors' ? 'get' : 'post';

      return apiAuthClient[method](`${endPoints[payload].get}/${process.env.VUE_APP_CONTRACT_ID}`, rootGetters['auth/getAccessToken']).then(response => {
          if (response.status === 200) {
            commit('UPDATE', {
              itemType: payload,
              data: response.data
            });
            return response;
          }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/fetchItems', retryPayload: payload }, { root: true });
        } else {
          return errorHandler('childEntityErrors', err, 'get');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false }, { root: true });
      });
    },
    fetchAssetItems ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Fetching your items...' }, { root: true });

      return apiAuthClient.get(`${endPoints.assets.get}/${payload.claimId}`, rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: 'assets',
            data: response.data
          });
          return response;
        }
    }).catch((err) => {
      if (err.response.status === 401) {
        dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/fetchAssetItems', retryPayload: payload }, { root: true });
      } else {
        return errorHandler('childEntityErrors', err, 'get');
      }
    }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false }, { root: true });
      });
    },
    addItem ({ commit, dispatch, rootGetters }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Adding your item...' }, { root: true });
      return apiAuthClient.post(`${endPoints[payload.itemType].add}`, [payload.data], rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('ADD', {
            itemType: payload.itemType,
            data: response.data[0]
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/addItem', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'post');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    editById ({ commit, dispatch, state, rootGetters }, payload) {
      const itemList = state[payload.itemType];
      const fieldId = endPoints[payload.itemType].fieldId;
      const itemToUpdate = itemList.findIndex(item => item[fieldId] === payload.data[fieldId]);

      itemList.splice(itemToUpdate, 1, payload.data);
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'editing your item...' }, { root: true });
      return apiAuthClient.put(`${endPoints[payload.itemType].edit}`, itemList, rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: payload.itemType,
            data: itemList
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/editById', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'put');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    deleteById ({ commit, state, dispatch, rootGetters }, payload) {
      const itemList = state[payload.itemType];
      const fieldId = endPoints[payload.itemType].fieldId;
      const itemToUpdate = itemList.findIndex(item => item[fieldId] === payload.id);
      const item = { ...itemList[itemToUpdate], statecode: 1 };

      itemList.splice(itemToUpdate, 1, item);

      dispatch('ui/updateInterstitial', { isLoading: true, message: 'deleting your item...' }, { root: true });
      return apiAuthClient.put(`${endPoints[payload.itemType].edit}`, [item], rootGetters['auth/getAccessToken']).then(response => {
        if (response.status === 200) {
          commit('UPDATE', {
            itemType: payload.itemType,
            data: itemList
          });
          return true;
        }
      }).catch((err) => {
        if (err.response.status === 401) {
          dispatch('auth/handleUnauthorised', { retryFunction: 'childEntities/deleteById', retryPayload: payload }, { root: true });
        } else {
          throw errorHandler('childEntityErrors', err, 'delete');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    setCurrentId ({ commit }, payload) {
      commit('SET_CURRENT_ID', payload);
    },
    getActivityTypeList ({ commit }) {
      return apiAuthClient.get(`icapplication/activity-types/${contractId}`, rootGetters['auth/getAccessToken']).then((res) => {
        const activityOptions = [];
        res.data.forEach((item) => {
            activityOptions.push({
              value: item.ua_icfundactivitytypeid,
              label: item.ua_activitytype
            });
        });

        commit('UPDATE_ACTIVITY_TYPES', activityOptions);
      });
    },
    getVerifiedSupplierList ({ commit }) {
      return apiAuthClient.get(`icapplication/GetICProjectSuppliers/${process.env.VUE_APP_CONTRACT_ID}`).then((res) => {
        const verifiedSuppliers = [];

      res.data.forEach(item => {
          // loop through suppliers and check if they have expertise associated to them
          if (item.ua_icsupplierexpertise) {
            const expertise = [];

            item.ua_icsupplierexpertise.forEach(expItem => {
              expertise.push(
                expItem.ua_name ? expItem.ua_name : 'Other'
              );
            });
            verifiedSuppliers.push({
              label: item.ua_suppliername,
              value: item.ua_suppliername,
              expertise: expertise
            });
            // If so loop through the expertise and add an item to the supplierExpertise array
          }
        });

        // Send the supplier expertise data to mutation
        commit('ADD_VERIFIED_SUPPLIER', verifiedSuppliers);
      });
    }
  },
  getters: {
    getItems: state => (itemType) => {
      const items = state[itemType];
      if (!items.length) {
        return items;
      }
      return items.filter(item => item.statecode !== 1);
    },
    getCurrentItemId (state) {
      return state.currentItemId;
    },
    getCurrentItem: state => (itemType) => {
      const fieldId = endPoints[itemType].fieldId;
      const result = state[itemType].find(item => item[fieldId] === state.currentItemId);
      return result;
    },
    getActivityTypes: (state) => {
      return state.activityTypes;
    },
    getActivityTypeName: state => (id) => {
      const type = state.activityTypes.find(item => item.value === id);
      return type ? type.label : '';
    },
    getSupplierName: state => (id) => {
      const supplier = state.verifiedSuppliers.find(item => item.value === id);
      return supplier ? supplier.label : '';
    },
    getActivityTotalCost: (state) => {
      const activities = state.activities;
      let totalCost = 0;
      if (activities.length) {
        activities.forEach((activity) => {
          if (activity.statecode !== 1) {
            const costToAdd = parseFloat(activity.ua_activitytotalcost).toFixed(2);
            totalCost += parseFloat(costToAdd);
          }
        });
      }
      return parseFloat(totalCost);
    },
    getTotalExtraFunds: (state) => {
      const extraFunds = state.extraFunds;
      let totalCost = 0;
      if (extraFunds.length) {
        extraFunds.forEach((extraFund) => {
          totalCost += parseInt(extraFund.ua_facilitylimit, 10);
        });
      }

      return totalCost;
    },
    getTotalJobs: (state) => {
      let total = 0;
      const sumFunction = (a, b) => a + b;

      state.jobs.forEach(job => {
        total = sumFunction(total, parseInt(job.ua_quantity));
      });
      return total;
    },
    getVerifiedSuppliers: (state) => {
      return state.verifiedSuppliers;
    }
  }
};
